import { Component, ChangeDetectionStrategy, Input, OnInit, EventEmitter, Output, OnDestroy } from '@angular/core';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { SetLabel, RemoveLabel } from 'src/app/story/state/labels/story.labels.actions';
import { LabelPinType } from 'src/app/story/models/labels/label-pin-type.enum';
import { LabelFlyTo } from 'src/app/story/models/labels/label-fly-to.enum';
import { CameraService } from 'src/app/engine/camera.service';
import { MatSnackBar } from '@angular/material';
import { LabelThemeEnum } from 'src/app/story/models/labels/label-theme.enum';
import { FlyToObjectErrandModel } from 'src/app/story/models/errands/fly-to-object-errand.model';
import { FlyToCameraErrandModel } from 'src/app/story/models/errands/fly-to-camera-errand.model';
import { LabelStateModel } from 'src/app/story/models/labels/label-state.model';
import { uuid } from 'src/app/shared/uuid';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {get, cloneDeep, isEqual } from 'lodash';

export interface LabelWidgetOpened { 
  label: LabelModel; 
  open: boolean;
}

@Component({
  selector: 'app-label-widget',
  templateUrl: './label-widget.component.html',
  styleUrls: ['./label-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LabelWidgetComponent implements OnInit, OnDestroy {
  labelTheme = LabelThemeEnum;
  labelPinType = LabelPinType;
  labelFlyTo = LabelFlyTo;

  private ngUnsubscribe = new Subject();

  @Input()
  public get label(): LabelModel {
    return this._label;
  }
  public set label(value: LabelModel) {
    const a = JSON.stringify(value);
    const b = JSON.stringify(this._label);

    if (a !== b) {
      this._label = value;
      this.setFormValues(value);
    }
  }
  private _label: LabelModel;

  @Input()
  open = false;

  @Output()
  opened = new EventEmitter<LabelWidgetOpened>();

  get _theme(): LabelThemeEnum {
    return this.form.get('theme').value;
  }
  get _flyTo(): boolean {
    return this.form.get('flyTo').value;
  }
  get _flyToTarget(): LabelFlyTo {
    return this.form.get('flyToTarget').value;
  }
  get _hideWire(): boolean {
    return this.form.get('hideWire').value;
  }


  form: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private store: Store,
    private cameraService: CameraService,
    private matSnackBar: MatSnackBar
  ) {
    const defaultLabel = new LabelModel('');
    this.form = this.formBuilder.group({
      title: [defaultLabel.title],
      description: [defaultLabel.description],
      theme: [defaultLabel.theme],
      pinType: [defaultLabel.pinType, Validators.required],
      pinGlow: [defaultLabel.pinGlow],
      collapseDescription: [defaultLabel.collapseDescription],
      hideWire: [defaultLabel.hideWire],
      hideTag: [defaultLabel.hideTag],
      expandTag: [defaultLabel.expandTag],
      showDescription: [defaultLabel.showDescription],
      flyTo: [false],
      flyToTarget: [''],
    });
  }

  ngOnInit() {
    this.form.valueChanges
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((values) => this.handleFormChanges(values));
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }


  handleAlignButton(left: number, top: number) {
    this.label.offset = [left, top];
    this.store.dispatch(new SetLabel(this.label));
  }

  handleRecordPositionClick() {
    this.label.states = this.prepareLabelStates(this.label.states);

    this.label.states[1].errands = [new FlyToCameraErrandModel(this.cameraService.getCamera())];
    this.store.dispatch(new SetLabel(this.label));
    this.matSnackBar.open('Recorded marked position');
  }

  handleRemove() {
    this.store.dispatch(new RemoveLabel(this.label));    
  }

  handleFormChanges(values) {
    const label = cloneDeep(this.label);

    // On annotation theme, limit functionality
    if (values.theme === LabelThemeEnum.Annotation) {
      values.hideWire = false;
      values.pinType = LabelPinType.Default;
    }

    label.title = values.title;
    label.description = values.description || '';
    label.theme = values.theme;
    label.pinType = values.pinType;
    label.pinGlow = values.pinGlow;
    label.collapseDescription = values.collapseDescription;
    label.hideWire = values.hideWire;
    label.hideTag = values.hideTag;
    
    label.expandTag = values.expandTag;
    label.showDescription = values.showDescription;


    // Parse fly to functionality
    if (values.flyTo) {
      label.states = this.prepareLabelStates(label.states);

      label.flyTo = values.flyTo;
      label.flyToTarget = values.flyToTarget;

      switch (values.flyToTarget) {
        case LabelFlyTo.Object:
          label.states[1].errands = [new FlyToObjectErrandModel(label.objectId)];
          break;
        
        case LabelFlyTo.Position:
          const errand = get(label, 'states[1].errands[0]');
          if (!(errand instanceof FlyToCameraErrandModel)) {
            label.states[1].errands = [new FlyToCameraErrandModel(this.cameraService.getCamera())];
          }
          break;
      }
    } else {
      if (label.states && label.states.length && label.states[1]) {
        label.states[1].errands = [];
      }
    }

    this.store.dispatch(new SetLabel(label));
  }

  handleAlignClick(direction: 'left' | 'right') {
    const label = cloneDeep(this.label);
    const amount = (label.hideWire) ? 20 : 32;

    switch (direction) {
      case 'left':
        label.offset = [-amount, 0];
        break;
      case 'right':
        label.offset = [amount, 0];
        break;
    }

    this.store.dispatch(new SetLabel(label));
  }

  handleOpened(open: boolean) {
    this.opened.emit({
      label: this.label,
      open: open
    });
  }

  prepareLabelStates(states: LabelStateModel[]) {
    let newStates = states ? [...states] : [];

    if (!newStates) {
      newStates = [];
    }

    if (!newStates[0]) {
      newStates[0] = new LabelStateModel();
      newStates[0].id = uuid.idWithPrefix('label-state');
    }

    if (!newStates[1]) {
      newStates[1] = new LabelStateModel();
      newStates[1].id = uuid.idWithPrefix('label-state');
    }

    return newStates;
  }

  setFormValues(label: LabelModel) {
    const values = {
      title: label.title,
      description: label.description || '',
      theme: label.theme,
      pinType: label.pinType,
      pinGlow: label.pinGlow,
      collapseDescription: label.collapseDescription,
      hideWire: label.hideWire,
      hideTag: label.hideTag,
      flyTo: null,
      flyToTarget: LabelFlyTo.Object,
      expandTag: label.expandTag,
      showDescription: label.showDescription
    };

    // Wire up fly to
    const errand = get(label, 'states[1].errands[0]');

    if (errand) {
      values.flyTo = true;
      switch (true) {
        case errand instanceof (FlyToCameraErrandModel): 
          values.flyToTarget = LabelFlyTo.Position;
          break;
        case errand instanceof (FlyToObjectErrandModel): 
        default:
          values.flyToTarget = LabelFlyTo.Object;
          break;
      }
    }

    this.form.setValue(values, { emitEvent: false });
  }

}
